import { Document, Page, Text, View } from '@react-pdf/renderer';
import React from 'react';
import {
  getReimbursmentSubmissionDate,
  EventReimbursementModule,
  Reimbursement,
} from '../../interfaces/reimbursment';
import { formatCurrency } from '../../libs/utils/formatters/currency';
import { formatDate } from '../../libs/utils/formatters/datetimeFormatter';
import StatusBadge from './ReimbursementStatusBadge';
import { styles } from './styles';

interface SpeakerReimbursementPDFProps {
  reimbursement: Reimbursement;
}

const SpeakerReimbursementPDF: React.FC<SpeakerReimbursementPDFProps> = ({
  reimbursement,
}) => {
  const reimbursementModule = reimbursement.attributes
    .module as EventReimbursementModule;

  const expensesTotal =
    (reimbursementModule.refund?.expenses?.length ?? 0) > 0
      ? (reimbursementModule.refund?.expenses?.reduce(
          (sum, expense) => sum + (expense.amount || 0),
          0,
        ) ?? 0)
      : (reimbursement.attributes.totalReimbursement ?? 0);

  const paymentAmount = reimbursementModule.payment?.amount || 0;

  const speakerExpensesTotal =
    reimbursementModule.refund?.expenses?.reduce(
      (sum, expense) =>
        expense.recipient === 'Relatore' || expense.recipient === 'Docente'
          ? sum + (expense.amount || 0)
          : sum,
      0,
    ) || 0;

  const onavExpensesTotal =
    reimbursementModule.refund?.expenses?.reduce(
      (sum, expense) =>
        expense.recipient === 'ONAV' ? sum + (expense.amount || 0) : sum,
      0,
    ) || 0;

  const eventBalance =
    reimbursement.attributes.event?.data?.attributes?.balance;
  const balancePaymentDate =
    eventBalance?.teacherPayments?.paymentDate ||
    eventBalance?.teacherRefunds?.paymentDate;
  const paymentDate =
    balancePaymentDate || reimbursementModule.simplifiedPaymentDate;

  const isReimbursementPaid = () => {
    if (reimbursementModule.simplifiedPaymentDate) return true;
    if (!eventBalance) return false;

    const hasPaymentAmount =
      !reimbursementModule.payment?.waivePayment &&
      (reimbursement.attributes.totalCompensation ?? 0) > 0;
    const hasRefundAmount =
      !reimbursementModule.refund?.waiveRefund &&
      (reimbursement.attributes.totalReimbursement ?? 0) > 0;

    if (!hasPaymentAmount && !hasRefundAmount) return false;

    const paymentPaid = !hasPaymentAmount || !!eventBalance.teacherPayments?.paymentDate;
    const refundPaid = !hasRefundAmount || !!eventBalance.teacherRefunds?.paymentDate;

    return paymentPaid && refundPaid;
  };
  const isPaid = isReimbursementPaid();
  const status = isPaid ? ('PAGATO' as const) : reimbursement.attributes.status;

  const submissionDate = formatDate(
    getReimbursmentSubmissionDate(reimbursement.attributes),
    'L',
  );

  const grossTotal =
    paymentAmount +
    (reimbursementModule.refund?.waiveRefund ? 0 : speakerExpensesTotal);
  const ritenutaAmount = reimbursementModule.payment?.waivePayment
    ? 0
    : grossTotal * 0.2;
  const netTotal = grossTotal - ritenutaAmount;

  return (
    <Document>
      <Page size='A4' style={styles.page}>
        <View style={styles.meta}>
          <View style={styles.metaRow}>
            <View>
              <Text style={styles.label}>Data invio</Text>
              <Text style={styles.value}>{submissionDate}</Text>
            </View>
            <View style={{ alignItems: 'center' }}>
              <StatusBadge status={status} />
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Dettagli Evento</Text>
          <View style={styles.row}>
            <View style={styles.col}>
              <Text style={styles.label}>Titolo</Text>
              <Text style={styles.value}>
                {reimbursement.attributes.event?.data?.attributes?.title ??
                  reimbursementModule.description}
              </Text>
            </View>
            <View style={styles.col}>
              <Text style={styles.label}>Data</Text>
              <Text style={styles.value}>
                {formatDate(
                  reimbursement.attributes.event?.data?.attributes?.startDate,
                  'LL',
                )}
              </Text>
            </View>
          </View>
          <View style={styles.row}>
            <View style={styles.col}>
              <Text style={styles.label}>Sezione</Text>
              <Text style={styles.value}>
                {
                  reimbursement.attributes.event?.data?.attributes?.section
                    ?.data?.attributes?.name
                }
              </Text>
            </View>
            <View style={styles.col}>
              <Text style={styles.label}>Ruolo</Text>
              <Text style={styles.value}>Relatore</Text>
            </View>
          </View>
          <View style={styles.row}>
            <View style={styles.col}>
              <Text style={styles.label}>Compenso preventivo</Text>
              <Text style={styles.value}>
                {formatCurrency(
                  eventBalance?.teacherPayments?.estimatedOutflow || 0,
                )}
              </Text>
            </View>
            <View style={styles.col}>
              <Text style={styles.label}>Rimborso preventivo</Text>
              <Text style={styles.value}>
                {formatCurrency(
                  eventBalance?.teacherRefunds?.estimatedOutflow || 0,
                )}
              </Text>
            </View>
          </View>
          {paymentDate && (
            <View style={styles.row}>
              <View style={styles.col}>
                <Text style={styles.label}>Data pagamento</Text>
                <Text style={styles.value}>{formatDate(paymentDate, 'L')}</Text>
              </View>
            </View>
          )}
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Dati Personali</Text>
          <View style={styles.row}>
            <View style={styles.col}>
              <Text style={styles.label}>Nome e Cognome</Text>
              <Text style={styles.value}>
                {`${reimbursementModule.personalInfo?.firstName} ${reimbursementModule.personalInfo?.lastName}`}
              </Text>
            </View>
            <View style={styles.col}>
              <Text style={styles.label}>Codice Fiscale</Text>
              <Text style={styles.value}>
                {reimbursementModule.personalInfo?.fiscalCode}
              </Text>
            </View>
          </View>
          <View style={styles.row}>
            <View style={styles.col}>
              <Text style={styles.label}>Indirizzo</Text>
              <Text style={styles.value}>
                {reimbursementModule.personalInfo?.address}
              </Text>
            </View>
            <View style={styles.col}>
              <Text style={styles.label}>Civico</Text>
              <Text style={styles.value}>
                {reimbursementModule.personalInfo?.streetNumber}
              </Text>
            </View>
          </View>
          <View style={styles.row}>
            <View style={styles.col}>
              <Text style={styles.label}>Città</Text>
              <Text style={styles.value}>
                {`${reimbursementModule.personalInfo?.city} (${reimbursementModule.personalInfo?.province}) ${reimbursementModule.personalInfo?.zip}`}
              </Text>
            </View>
            <View style={styles.col}>
              <Text style={styles.label}>Email</Text>
              <Text style={styles.value}>
                {reimbursementModule.personalInfo?.email}
              </Text>
            </View>
          </View>
          <View style={styles.row}>
            <View style={styles.col}>
              <Text style={styles.label}>Telefono</Text>
              <Text style={styles.value}>
                {reimbursementModule.personalInfo?.phone}
              </Text>
            </View>
            <View style={styles.col}>
              <Text style={styles.label}>IBAN</Text>
              <Text style={styles.value}>
                {reimbursementModule.personalInfo?.iban}
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Compenso</Text>
          {reimbursementModule.payment?.waivePayment ? (
            <>
              <View style={styles.row}>
                <View style={styles.col}>
                  <Text style={styles.label}>Rinuncia al compenso</Text>
                  <Text style={styles.value}>
                    Il relatore ha rinunciato al compenso
                  </Text>
                </View>
              </View>
              <View style={styles.row}>
                <View style={styles.col}>
                  <Text style={styles.label}>Evento</Text>
                  <Text style={styles.value}>
                    {reimbursementModule.payment?.eventName ||
                      reimbursementModule.description}
                  </Text>
                </View>
                <View style={styles.col}>
                  <Text style={styles.label}>Sezione</Text>
                  <Text style={styles.value}>
                    {reimbursementModule.payment?.organizingSection}
                  </Text>
                </View>
              </View>
            </>
          ) : (
            <>
              <View style={styles.row}>
                <View style={styles.col}>
                  <Text style={styles.label}>Importo</Text>
                  <Text style={styles.value}>
                    {formatCurrency(reimbursementModule.payment?.amount || 0)}
                  </Text>
                </View>
                <View style={styles.col}>
                  <Text style={styles.label}>Evento</Text>
                  <Text style={styles.value}>
                    {reimbursementModule.payment?.eventName}
                  </Text>
                </View>
              </View>
              <View style={styles.row}>
                <View style={styles.col}>
                  <Text style={styles.label}>Data Evento</Text>
                  <Text style={styles.value}>
                    {formatDate(
                      reimbursementModule.payment?.eventDate || '',
                      'L',
                    )}
                  </Text>
                </View>
                <View style={styles.col}>
                  <Text style={styles.label}>Sezione</Text>
                  <Text style={styles.value}>
                    {reimbursementModule.payment?.organizingSection}
                  </Text>
                </View>
              </View>

              <View style={{ marginTop: 10 }}>
                <Text style={[styles.label, { marginBottom: 5 }]}>
                  Dichiarazioni
                </Text>
                {!reimbursementModule.payment?.declarations ? (
                  <Text style={styles.value}>
                    Nessuna dichiarazione disponibile
                  </Text>
                ) : (
                  <>
                    {reimbursementModule.payment?.declarations
                      ?.isPublicEmployee && (
                      <Text style={styles.value}>
                        Dipendente pubblico:{' '}
                        {reimbursementModule.payment?.declarations
                          ?.isPublicEmployee === 'YES'
                          ? 'Sì'
                          : 'No'}
                      </Text>
                    )}

                    {Object.entries(
                      reimbursementModule.payment?.declarations
                        ?.autonomousWork || {},
                    ).map(([key, value]) => (
                      <Text key={key} style={styles.value}>
                        {value ? '✔︎' : '✗'}{' '}
                        {
                          {
                            noTimeConstraints:
                              'Il sottoscritto non ha avuto obblighi di orario',
                            discretionaryExecution:
                              "Il sottoscritto è stato pienamente libero di svolgere l'incarico con discrezionalità nei riguardi delle modalità e dei tempi di esecuzione",
                            independentCriteria:
                              "I criteri seguiti nell'effettuare la prestazione sono stati del tutto indipendenti e personali, avendo il richiedente solo indicato il contenuto della prestazione richiesta",
                            agreedCompensation:
                              "la remunerazione è quella dovuta sulla base dell'offerta concordata",
                          }[key]
                        }
                      </Text>
                    ))}

                    {Object.entries(
                      reimbursementModule.payment?.declarations || {},
                    )
                      .filter(
                        ([key]) =>
                          ![
                            'autonomousWork',
                            'isPublicEmployee',
                            'year',
                          ].includes(key),
                      )
                      .map(([key, value]) => (
                        <Text key={key} style={styles.value}>
                          {value ? '✔︎' : '✗'}{' '}
                          {
                            {
                              taskCompletion:
                                "di aver svolto l'incarico/chi sopra riportato/i",
                              vatExemption:
                                "che non esercito per professione abituale attività di lavoro autonomo e che il compenso non è soggetto ad IVA (escluso ai sensi dell'art.5 del D.P.R. 633/72)",
                              taxWithholding:
                                "che il compenso è soggetto a ritenuta di acconto ai sensi dell'art.25 del D.P.R. 600/73",
                              socialSecurity:
                                "che tale compenso non è assoggettato a contributo previdenziale ai sensi dell'art. 44 D.L. 269/209 in quanto nel corso dell'anno solare sotto indicato il totale dei compensi ricevuti dal prestatore non supera i 5.000,00€",
                              noEmploymentClaim:
                                'che la suddetta prestazione resa occasionalmente, esclude da parte del dichiarante qualsiasi pretesa di riconoscimento di rapporto di lavoro dipendente',
                            }[key]
                          }
                          {key === 'socialSecurity' &&
                            ` (Anno ${reimbursementModule.payment?.declarations?.year})`}
                        </Text>
                      ))}
                  </>
                )}
              </View>
            </>
          )}
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Spese</Text>
          {reimbursementModule.refund?.waiveRefund ? (
            <View style={styles.row}>
              <View style={styles.col}>
                <Text style={styles.label}>Rinuncia al rimborso</Text>
                <Text style={styles.value}>
                  Il relatore ha rinunciato al rimborso spese
                </Text>
              </View>
            </View>
          ) : (
            <>
              {reimbursementModule.refund?.expenses?.map((expense, index) => (
                <View key={index} style={styles.expenseItem}>
                  <View style={styles.row}>
                    <View style={{ ...styles.col, flex: 1 }}>
                      <Text style={styles.label}>Tipo</Text>
                      <Text style={styles.value}>{expense.type}</Text>
                    </View>
                    <View style={{ ...styles.col, flex: 1 }}>
                      <Text style={styles.label}>Descrizione</Text>
                      <Text style={styles.value}>{expense.description}</Text>
                    </View>
                    <View style={{ ...styles.col, flex: 1 }}>
                      <Text style={styles.label}>Data</Text>
                      <Text style={styles.value}>
                        {formatDate(expense.startDate, 'L')}
                        {expense.endDate &&
                          ` - ${formatDate(expense.endDate, 'L')}`}
                      </Text>
                    </View>
                    <View style={{ ...styles.col, flex: 0.5 }}>
                      <Text style={styles.label}>Intestata a</Text>
                      <Text style={styles.value}>{expense.recipient}</Text>
                    </View>
                    <View style={{ ...styles.col, flex: 0.5 }}>
                      <Text style={styles.label}>Importo</Text>
                      <Text style={styles.value}>
                        {formatCurrency(expense.amount)}
                      </Text>
                    </View>
                  </View>

                  {expense.type === 'Viaggio auto' && (
                    <View style={{ ...styles.row, marginTop: 2 }}>
                      <View style={{ ...styles.col, flex: 1 }}>
                        <Text style={styles.label}>Auto</Text>
                        <Text style={styles.value}>
                          {expense.carModel} - {expense.licensePlate}
                        </Text>
                      </View>
                      <View style={{ ...styles.col, flex: 1 }}>
                        <Text style={styles.label}>Kilometri</Text>
                        <Text style={styles.value}>
                          {expense.kilometers} km
                        </Text>
                      </View>
                      <View style={{ ...styles.col, flex: 1 }}></View>
                      <View style={{ ...styles.col, flex: 0.5 }}></View>
                      <View style={{ ...styles.col, flex: 0.5 }}></View>
                    </View>
                  )}
                </View>
              ))}
            </>
          )}
        </View>

        <View style={styles.summary}>
          <Text style={styles.sectionTitle}>Riepilogo Totali</Text>
          <View style={styles.summaryRow}>
            <Text style={styles.label}>TOTALE LORDO COMPENSO</Text>
            <Text style={styles.value}>{formatCurrency(paymentAmount)}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.label}>TOTALE LORDO RIMBORSO</Text>
            <Text style={styles.value}>{formatCurrency(expensesTotal)}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.label}>TOTALE LORDO</Text>
            <Text style={styles.value}>
              {formatCurrency(paymentAmount + expensesTotal)}
            </Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.label}>RITENUTA D'ACCONTO (20%)</Text>
            <Text style={styles.value}>{formatCurrency(ritenutaAmount)}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.label}>TOTALE NETTO</Text>
            <Text style={styles.value}>{formatCurrency(netTotal)}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.label}>FATTURE INTESTATE A ONAV</Text>
            <Text style={styles.value}>
              {formatCurrency(onavExpensesTotal)}
            </Text>
          </View>
        </View>

        {reimbursement.attributes.note && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Note</Text>
            <Text style={styles.value}>{reimbursement.attributes.note}</Text>
          </View>
        )}

        <View style={styles.signature}>
          <Text style={styles.label}>Firma</Text>
          <Text style={styles.value}>{reimbursementModule.signature}</Text>
        </View>
      </Page>
    </Document>
  );
};

export default SpeakerReimbursementPDF;
